/**
 * A receipt photo taken on a phone right after the buy run (BLU-1268, the
 * just-in-time PO page). A phone camera writes 4000+ px HEIC or JPEG files of
 * several MB, and the PO file upload is base64 inside a JSON body with a 7 MB
 * cap. So before upload:
 *
 *   1. HEIC / HEIF becomes JPEG (`maybeConvertHeic`, lazy-loaded decoder);
 *   2. an image longer than `RECEIPT_MAX_SIDE_PX` is redrawn at that size as a
 *      JPEG at `RECEIPT_JPEG_QUALITY`, plenty to read a receipt.
 *
 * A PDF, a small image, or any failure (no canvas, decode error) keeps the
 * file as picked: a receipt is never lost to a resize.
 */
import { maybeConvertHeic } from '@/lib/heic-convert';

export const RECEIPT_MAX_SIDE_PX = 2000;
export const RECEIPT_JPEG_QUALITY = 0.8;
/** The PO file upload's cap (purchase-orders.service, uploadPurchaseOrderFile). */
export const RECEIPT_MAX_BYTES = 7 * 1024 * 1024;

const canvasToJpeg = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', RECEIPT_JPEG_QUALITY));

async function downscaleImage(file: File): Promise<File> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width, bitmap.height);
    if (longest <= RECEIPT_MAX_SIDE_PX) return file;
    const scale = RECEIPT_MAX_SIDE_PX / longest;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await canvasToJpeg(canvas);
    if (!blob || blob.size >= file.size) return file;
    const base = file.name.replace(/\.[^.]+$/, '') || 'receipt';
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    bitmap?.close();
  }
}

/** The file to upload for a picked receipt (converted and downscaled when it is a photo). */
export async function prepareReceiptFile(file: File): Promise<File> {
  const { file: decoded } = await maybeConvertHeic(file);
  if (!decoded.type.startsWith('image/')) return decoded;
  return downscaleImage(decoded);
}
